/**
 * CustomFieldForm Component
 *
 * A comprehensive form component for creating and editing custom fields with conditional logic.
 * Supports various field types including text, number, dropdown, boolean, and file uploads.
 * Implements advanced conditional field logic where fields can be shown/hidden based on
 * dependent field values.
 *
 * Features:
 * - Dynamic field type configuration (text, number, dropdown, boolean, file)
 * - Conditional field logic with dependent field relationships
 * - Validation and error handling
 * - Internationalization support
 * - Duplicate prevention for field options
 * - Smart filtering of available dependent fields
 *
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.record - Current custom field record being edited (null for new fields)
 * @param {Object} props.customFieldSectionRecord - Parent section containing available fields
 */

import { Field, useFormState } from "react-final-form";
import { FieldArray } from "react-final-form-arrays";
import { useIntl } from "react-intl";
import { connect } from "react-redux";
import * as Validations from "../../../common/form/validator";
import {
  DropdownInput,
  SwitchInput,
  Tags,
  TextInput,
  MultiSelectInput,
} from "../../../common/fields";
import { GlobalConstants } from "../../../../constants";

const CustomFieldForm = ({ record, customFieldSectionRecord }) => {
  const formState = useFormState();
  const intl = useIntl();

  // Configure default value input type based on field type
  // Type 3 (dropdown) and Type 4 (boolean) use dropdown inputs
  let defaultValueType = "text";
  let defaultValueOptions = [];
  if (formState.values.type === 3 || formState.values.type === 4) {
    defaultValueType = "dropdown";
  }

  // Generate options for default value field based on field type
  if (formState.values.type === 3) {
    // For dropdown fields, use the configured options
    defaultValueOptions =
      formState.values.options?.map((option) => ({
        name: option,
        value: option,
      })) || [];
  } else if (formState.values.type === 4) {
    // For boolean fields, use yes/no labels with standardized values
    defaultValueOptions = [
      { name: formState.values.yes_label, value: "yes" },
      { name: formState.values.no_label, value: "no" },
    ];
  }

  /**
   * Filter available fields that can be used as dependent fields in conditions.
   *
   * Rules:
   * - Only fields with lower order than current field (or all fields for new records)
   * - Dropdown/Boolean fields: Only included if they have valid options
   * - Other field types: Always included
   *
   * Field Types:
   * - Type 1: Text
   * - Type 2: Number
   * - Type 3: Dropdown (requires options)
   * - Type 4: Boolean (requires options)
   * - Type 5: Date
   * - Type 6: TextLong
   */

  let conditionFields =
    customFieldSectionRecord?.fields.filter((field) => {
      // Only include fields with order less than current field
      if (record && field.order >= record?.order) return false;

      // For dropdown and boolean fields, only include if they have valid options
      if (field.type === 3 || field.type === 4) {
        return (
          field.options &&
          field.options.length > 0 &&
          field.options.some((option) => option && option.trim() !== "")
        );
      }

      // Include all other field types without any conditions
      return true;
    }) || [];

  /**
   * Get dependent field details by ID from the available condition fields.
   *
   * @param {string} dependentFieldId - The ID of the dependent field to find
   * @returns {Object|undefined} The field object or undefined if not found
   */
  const getDependentFieldDetails = (dependentFieldId) => {
    return conditionFields.find((field) => field.id === dependentFieldId);
  };

  /**
   * Generate options array for dropdown/boolean fields based on field type.
   *
   * @param {number} fieldType - Field type (3 for dropdown, 4 for boolean)
   * @param {Array} options - Array of option strings for dropdown fields
   * @param {string} yesLabel - Label for "yes" option in boolean fields
   * @param {string} noLabel - Label for "no" option in boolean fields
   * @returns {Array} Array of {name, value} option objects
   */
  const getFieldOptions = (fieldType, options, yesLabel, noLabel) => {
    if (fieldType === 3) {
      // Dropdown - remove duplicates and filter out empty values
      const uniqueOptions = [...new Set(options || [])].filter(
        (option) => option && option.trim() !== ""
      );
      return uniqueOptions.map((option) => ({
        name: option,
        value: option,
      }));
    } else if (fieldType === 4) {
      // Boolean - simple approach with fallback
      const yes = (yesLabel && yesLabel.trim()) || "Yes";
      const no = (noLabel && noLabel.trim()) || "No";

      return [
        { name: yes, value: "yes" },
        { name: no, value: "no" },
      ];
    }
    return [];
  };

  /**
   * Get options for the main field (current field being created/edited).
   * Filters out already used values for boolean fields to prevent duplicates.
   *
   * @param {Array} usedValues - Array of values already used in other conditions
   * @returns {Array} Array of available option objects
   */
  const getMainFieldOptions = (usedValues = []) => {
    let options = getFieldOptions(
      formState.values.type,
      formState.values.options,
      formState.values.yes_label,
      formState.values.no_label
    );

    // Filter out used values to prevent duplicates
    if (usedValues.length > 0) {
      options = options.filter((option) => !usedValues.includes(option.value));
    }

    return options;
  };

  /**
   * Check if more conditions can be added based on field type and availability.
   *
   * Rules:
   * - Boolean main fields: Maximum 2 conditions (yes/no)
   * - Other fields: Can add conditions if dependent fields are available
   *
   * @param {Object} fields - FieldArray fields object
   * @returns {boolean} Whether more conditions can be added
   */
  const canAddMoreConditions = (fields) => {
    // For boolean main field, limit to 2 conditions maximum
    if (formState.values.type === 4 && fields.length >= 2) {
      return false;
    }

    // For all other cases, allow adding conditions if there are available dependent fields
    return conditionFields.length > 0;
  };

  /**
   * Get available options for a dependent field, filtering out already used values.
   * Prevents creating duplicate conditions with the same dependent field + expected value.
   *
   * @param {Object} dependentField - The dependent field object
   * @param {number} currentIndex - Index of current condition being edited (to exclude from filtering)
   * @returns {Array} Array of available option objects for the dependent field
   */
  const getDependentFieldOptions = (dependentField, currentIndex = -1) => {
    if (!dependentField) return [];

    let options = getFieldOptions(
      dependentField.type,
      dependentField.options,
      dependentField.options?.[0], // Use first option as "yes" label for boolean
      dependentField.options?.[1] // Use second option as "no" label for boolean
    );

    // Filter out already used expected values for the same dependent field
    if (formState.values.conditions && formState.values.conditions.length > 0) {
      const usedValues = formState.values.conditions
        .filter(
          (condition, index) =>
            index !== currentIndex && // Exclude current condition
            condition.dependentFieldId === dependentField.id && // Same dependent field
            condition.expectedValue // Has expected value
        )
        .map((condition) => condition.expectedValue);

      // Remove already used values from options
      options = options.filter((option) => !usedValues.includes(option.value));
    }

    return options;
  };

  return (
    <>
      {/* Main Field Configuration Section */}
      <div className="grid">
        {/* Basic Field Information */}
        <div className="col-12 md:col-6 lg:col-4">
          <Field
            name="name"
            component={TextInput}
            type="text"
            label={intl.formatMessage({
              id: "name",
            })}
            placeholder={intl.formatMessage({
              id: "name",
            })}
            required
            validate={Validations.composeValidators(
              Validations.required,
              Validations.validateAplahbetsAndSpace
            )}
          />
        </div>
        <div className="col-12 md:col-6 lg:col-4">
          <Field
            name="label"
            component={TextInput}
            type="text"
            label={intl.formatMessage({
              id: "label",
            })}
            placeholder={intl.formatMessage({
              id: "label",
            })}
            required
            validate={Validations.composeValidators(
              Validations.required,
              Validations.validateAplahbetsAndSpace
            )}
          />
        </div>
        <div className="col-12 md:col-6 lg:col-4">
          <Field
            name="type"
            options={GlobalConstants.FIELD_TYPES}
            component={DropdownInput}
            label={intl.formatMessage({
              id: "type",
            })}
            placeholder={intl.formatMessage({
              id: "type",
            })}
            readOnly={record && record.id}
            required
            validate={Validations.composeValidators(Validations.required)}
          />
        </div>
        <div className="col-12 md:col-6 lg:col-4">
          <Field
            name="showOnRegistration"
            component={SwitchInput}
            type="checkbox"
            label={intl.formatMessage({
              id: "showOnRegistration",
            })}
            placeholder={intl.formatMessage({
              id: "showOnRegistration",
            })}
          />
        </div>
        <div className="col-12 md:col-6 lg:col-4">
          <Field
            name="isRequired"
            component={SwitchInput}
            type="checkbox"
            label={intl.formatMessage({
              id: "required",
            })}
            placeholder={intl.formatMessage({
              id: "required",
            })}
          />
        </div>
        {/* Dropdown Field Options - Only shown for Type 3 (Dropdown) */}
        {(formState.values.type === 3 && (
          <div className="col-12 md:col-6">
            <Field
              name="options"
              component={Tags}
              label={intl.formatMessage({
                id: "options",
              })}
              placeholder={intl.formatMessage({
                id: "options",
              })}
              removeOptionsDisabled={(record && record.id && true) || false}
              required
              validate={Validations.composeValidators(Validations.required)}
            />
          </div>
        )) ||
          null}
        {/* Boolean Field Labels - Only shown for Type 4 (Boolean) */}
        {(formState.values.type === 4 && (
          <>
            <div className="col-12 md:col-6">
              <Field
                name="yes_label"
                component={TextInput}
                type="text"
                label={intl.formatMessage({
                  id: "yes_label",
                })}
                placeholder={intl.formatMessage({
                  id: "yes_label",
                })}
                required
                validate={Validations.composeValidators(Validations.required)}
              />
            </div>
            <div className="col-12 md:col-6">
              <Field
                name="no_label"
                component={TextInput}
                type="text"
                label={intl.formatMessage({
                  id: "no_label",
                })}
                placeholder={intl.formatMessage({
                  id: "no_label",
                })}
                required
                validate={Validations.composeValidators(Validations.required)}
              />
            </div>
          </>
        )) ||
          null}
        {/* Default Value Field - Hidden for Type 5 (Date) and Type 6 (TextLong) */}
        {(formState.values.type !== 5 && formState.values.type !== 6 && (
          <div className="col-12 md:col-6">
            <Field
              name="defaultValue"
              component={
                defaultValueType === "dropdown" ? DropdownInput : TextInput
              }
              options={defaultValueOptions}
              type="text"
              label={intl.formatMessage({
                id: "default_value",
              })}
              placeholder={intl.formatMessage({
                id: "default_value",
              })}
            />
          </div>
        )) ||
          null}
      </div>

      {/* TextLong Fields - maxLength and placeholder (Type 6 only) */}
      {formState.values.type === 6 && (
        <div className="grid">
          <div className="col-12 md:col-6">
            <Field
              name="maxLength"
              component={TextInput}
              type="number"
              label={intl.formatMessage({ id: "max_length" })}
              placeholder={intl.formatMessage({ id: "enter_max_length" })}
              min="1"
              max="10000"
            />
          </div>
          <div className="col-12 md:col-6">
            <Field
              name="placeholder"
              component={TextInput}
              type="text"
              label={intl.formatMessage({ id: "placeholder" })}
              placeholder={intl.formatMessage({ id: "enter_placeholder" })}
            />
          </div>
        </div>
      )}

      {/* Conditional Logic Section - Only shown if dependent fields are available */}
      {(conditionFields.length && (
        <div>
          <h3>{intl.formatMessage({ id: "conditions" })}</h3>
          <FieldArray name="conditions">
            {({ fields }) => (
              <div>
                {fields.map((name, index) => {
                  // Get current condition data and dependent field details
                  const currentCondition = formState.values.conditions?.[index];
                  const dependentField = getDependentFieldDetails(
                    currentCondition?.dependentFieldId
                  );

                  // Calculate available options for dependent field (filtering used values)
                  const dependentFieldOptions = getDependentFieldOptions(
                    dependentField,
                    index
                  );

                  // Determine if dependent field uses dropdown/boolean input
                  const isDropdownOrBoolean =
                    dependentField?.type === 3 || dependentField?.type === 4;

                  // Calculate main field options for fieldOptions field
                  const isMainFieldDropdownOrBoolean =
                    formState.values.type === 3 || formState.values.type === 4;

                  let mainFieldOptions = [];
                  if (isMainFieldDropdownOrBoolean) {
                    // For boolean main fields, filter out already used field options
                    if (formState.values.type === 4) {
                      const usedFieldOptions =
                        formState.values.conditions
                          ?.filter(
                            (condition, idx) =>
                              idx !== index && condition.fieldOptions
                          )
                          .map((condition) => condition.fieldOptions) || [];
                      mainFieldOptions = getMainFieldOptions(usedFieldOptions);
                    } else {
                      // For dropdown main fields, no filtering needed
                      mainFieldOptions = getMainFieldOptions();
                    }
                  }

                  return (
                    <div
                      key={name}
                      className="grid mb-3 p-3 border-1 border-round surface-border"
                    >
                      {/* Dependent Field Selection */}
                      <div className="col-12 md:col-6">
                        <Field
                          name={`${name}.dependentFieldId`}
                          component={DropdownInput}
                          label={intl.formatMessage({ id: "dependent_field" })}
                          placeholder={intl.formatMessage({
                            id: "select_dependent_field",
                          })}
                          options={conditionFields
                            .filter((field) => {
                              // For dropdown and boolean fields, check if they have available options
                              if (field.type === 3 || field.type === 4) {
                                const availableOptions =
                                  getDependentFieldOptions(field, index);
                                return availableOptions.length > 0;
                              }
                              // For other field types, always include them
                              return true;
                            })
                            .map((field) => ({
                              name: field.label,
                              value: field.id,
                            }))}
                          required
                          validate={Validations.required}
                        />
                      </div>
                      <div className="col-12 md:col-6">
                        <Field
                          name={`${name}.expectedValue`}
                          component={
                            isDropdownOrBoolean ? DropdownInput : TextInput
                          }
                          options={
                            isDropdownOrBoolean
                              ? dependentFieldOptions
                              : undefined
                          }
                          type={isDropdownOrBoolean ? undefined : "text"}
                          label={intl.formatMessage({ id: "expected_value" })}
                          placeholder={intl.formatMessage({
                            id: isDropdownOrBoolean
                              ? "select_expected_value"
                              : "enter_expected_value",
                          })}
                          required
                          validate={Validations.required}
                        />
                      </div>

                      {/* Field Options - What options to show for main field when condition is met */}
                      <div className="col-12 md:col-6">
                        <Field
                          name={`${name}.fieldOptions`}
                          component={
                            formState.values.type === 3
                              ? MultiSelectInput // Multiple options for dropdown main field
                              : isMainFieldDropdownOrBoolean
                              ? DropdownInput // Single option for boolean main field
                              : TextInput // Free text for other field types
                          }
                          options={
                            isMainFieldDropdownOrBoolean
                              ? mainFieldOptions
                              : undefined
                          }
                          showAsTags={false}
                          type={
                            isMainFieldDropdownOrBoolean ? undefined : "text"
                          }
                          label={intl.formatMessage({ id: "field_options" })}
                          placeholder={intl.formatMessage({
                            id:
                              formState.values.type === 3
                                ? "select_field_options_multiple"
                                : isMainFieldDropdownOrBoolean
                                ? "select_field_option"
                                : "enter_field_options",
                          })}
                          infoMessage="field_options_tooltip"
                        />
                      </div>

                      {/* Remove Condition Button */}
                      <div className="col-12 md:col-2 flex align-items-start">
                        <button
                          type="button"
                          className="p-button p-component p-button-danger p-button-outlined w-full mt-auto"
                          style={{ marginTop: "1.75rem" }}
                          onClick={() => fields.remove(index)}
                        >
                          <i className="pi pi-trash mr-2"></i>
                          {intl.formatMessage({ id: "remove" })}
                        </button>
                      </div>
                    </div>
                  );
                })}

                {/* Add New Condition Section */}
                <div className="mt-3">
                  <button
                    type="button"
                    className="p-button p-component p-button-outlined"
                    onClick={() =>
                      fields.push({
                        dependentFieldId: "",
                        expectedValue: "",
                        fieldOptions: "",
                      })
                    }
                    disabled={!canAddMoreConditions(fields)}
                  >
                    <i className="pi pi-plus mr-2"></i>
                    {intl.formatMessage({ id: "add_condition" })}
                    {formState.values.type === 4 && ` (${fields.length}/2)`}
                  </button>
                  {formState.values.type === 4 && fields.length >= 2 && (
                    <small className="block mt-2 text-orange-500">
                      {intl.formatMessage({ id: "boolean_condition_limit" })}
                    </small>
                  )}
                </div>
              </div>
            )}
          </FieldArray>
        </div>
      )) ||
        null}
    </>
  );
};

/**
 * Redux state mapping to inject custom field section data.
 *
 * @param {Object} state - Redux state
 * @returns {Object} Props object with customFieldSectionRecord
 */
const mapStateToProps = (state) => ({
  customFieldSectionRecord: state.customFields.record,
});

export default connect(mapStateToProps)(CustomFieldForm);
